import { StyledDocument } from "../Document.styled";

//react hooks
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

// zod
import { zodResolver } from "@hookform/resolvers/zod";

// models
import { Document, DocumentAdd, DocumentAddSchema } from "../../../../models/document";
import { DocumentSettings } from "../../../../models/document-settings";
import { DocumentStructure } from "../../../../models/document-structure";
import { DocumentItemAdd } from "../../../../models/document-item";
import { DocumentWorkAdd } from "../../../../models/document-work";
import { DocumentPaymentForm } from "../../../../models/document-payment";
import { Account } from "../../../../models/account";
import { DocumentClosingForm } from "../../../../models/document-closing";
import { DocumentDepositAdd } from "../../../../models/document-deposit";


// services
import { calcKabala, calcWorksAndItems, getNextDocumentNumber } from "../../../../services/documentsService";
import { addDocument as addDocumentService } from "../../../../services/documentsService";

// mui
import { Button } from "@mui/material"

// toast
import { toast } from 'react-toastify';
import { handleApiErrors, toastErrorHandling } from "../../../../services/errorHandling";

// components


import Header from "../components/header/Header";
import DocumentCar from "../components/document-car/DocumentCar";
import DocumentBank from "../components/document-bank/DocumentBank";
import DocumentWorks from "../components/document-works/DocumentWorks";
import DocumentItems from "../components/document-items/DocumentItems";
import Invoices from "../components/invoices/Invoices";
import Payments from "../components/payments/Payments";
import Deposit from "../components/deposit/Deposit";
import Title from "../components/title/Title";
import Summary from "../components/summary/Summary";

// import { DevTool } from '@hookform/devtools';

import { useErrorBoundary } from "react-error-boundary";
import { UserData } from "../../../../models/user-data";
import { getDocumentFormData, getDocumentSettings, getDocumentStructure, getFormItemsRows, getFormWorksRows, getPaymentsRows, parseDocumentSettingsIdToNumber, updateCustomDataArray } from "./AddDocument.utils";
import { useSearchParams } from 'react-router-dom';
import ReturnButton from "../../../../components/return-button/ReturnButton";
import { useGetDocumentsSettingsQuery } from "../../../../redux/api/documents-settings.api";
import { useGetCashBoxesAccountsQuery, useGetIncomesAccountsQuery } from "../../../../redux/api/accounts.api";
import { useGetDocumentsStructureQuery } from "../../../../redux/api/documents-structure.api";

import DocumentAccount from "../components/document-account/DocumentAccount";
import { ClosingFormSchema, ClostingFormType, DepositsFormSchema, DepostitsFormType, ItemsFormSchema, ItemsFormType, PaymentsFormSchema, PaymentsFormType, WorksFormSchema, WorksFormType } from "./AddDocument.types";
import { StyledSection } from "../../../../styles/components/Section.styled";
import Loading from "../../../loading/Loading";




function AddDocument() {


    // ###################################################################################### //
    // #                                 variables                                          # //
    // ###################################################################################### //




    const method = "add";

    const navigate = useNavigate();
    const { state } = useLocation() as { state: UserData }; // state data from another page
    
    const { showBoundary } = useErrorBoundary();
    const [searchParams] = useSearchParams();


    const documentSettingsId: number | null = parseDocumentSettingsIdToNumber(searchParams.get('documentType'));

    
    const { data: documentsSettings }: { data?: DocumentSettings[] } = useGetDocumentsSettingsQuery();
    const { data: documentsStructure }: { data?: DocumentStructure[] }  = useGetDocumentsStructureQuery();
    const { data: incomes }: { data?: Account[] } = useGetIncomesAccountsQuery();
    const { data: kupaAccounts }: { data?: Account[] } = useGetCashBoxesAccountsQuery();

    const settings: DocumentSettings | undefined = useMemo(()=> getDocumentSettings(documentsSettings, documentSettingsId), [documentsSettings, documentSettingsId]);
    const structure: DocumentStructure[] | undefined = useMemo(()=> getDocumentStructure(documentsStructure, documentsSettings, documentSettingsId), [documentsStructure, documentsSettings, documentSettingsId]);

    
    const [isSelectDepositsDialogOpen, setIsSelectDepositsDialogOpen] = useState<boolean>(false); // set if open select deposit dialog
    const [isSelectCashDialogOpen, setIsSelectCashDialogOpen] = useState<boolean>(false); // set if open select deposit dialog
    const [documentsToClose, setDocumentsToClose] = useState<Document[]>(); // invoices to close for receipt
    const [isSaving, setIsSaving] = useState<boolean>(false); // set if this doc is saving data right now

    

    // ###################################################################################### //
    // #                                   forms                                            # //
    // ###################################################################################### //


    // contain the account + car + document time and calc
    const documentForm = useForm<DocumentAdd>({
        resolver: zodResolver(DocumentAddSchema),
        defaultValues: getDocumentFormData(state)
    });

    // contain works data
    const worksForm = useForm<WorksFormType>({
        resolver: zodResolver(WorksFormSchema),
        defaultValues: {
            works: getFormWorksRows(state)
        }
    });

    // contain items data
    const itemsForm = useForm<ItemsFormType>({
        resolver: zodResolver(ItemsFormSchema),
        defaultValues: {
            items: getFormItemsRows(state)
        }
    });

    // contain the payments methods user added in kabala
    const paymentsForm = useForm<PaymentsFormType>({
        resolver: zodResolver(PaymentsFormSchema),
        defaultValues: {
            payments: getPaymentsRows(state)
        }
    });

    // contain the documents user choose to pay and close
    const documentClosingForm = useForm<ClostingFormType>({
        resolver: zodResolver(ClosingFormSchema),
        defaultValues: {
            documents: []
        }
    });

    // contain the deposits user choose
    const documentDepositsForm = useForm<DepostitsFormType>({
        resolver: zodResolver(DepositsFormSchema),
        defaultValues: {
            deposits: []
        }
    });


    const formDocumentSettingsId: number | undefined = useWatch({ control: documentForm.control, name: "document_settings_id"});
    const formDocumentNumber: number | undefined = useWatch({ control: documentForm.control, name: "document_number"});



    // ###################################################################################### //
    // #                              forms arrays                                          # //
    // ###################################################################################### //



    const itemsFieldArray = useFieldArray({
        name: 'items',
        control: itemsForm.control
    });

    const worksFieldArray = useFieldArray({
        name: 'works',
        control: worksForm.control
    });

    const paymentsFieldArray = useFieldArray({
        name: 'payments',
        control: paymentsForm.control
    });

    const documentsClosingFieldArray = useFieldArray({
        name: 'documents',
        control: documentClosingForm.control
    });

    const depositsFieldArray = useFieldArray({
        name: 'deposits',
        control: documentDepositsForm.control
    });




    // ###################################################################################### //
    // #                               use effects                                          # //
    // ###################################################################################### //




    // actions after getting the document settings.
    useEffect(() => {
        if (settings) {
            setDocumentNumber(settings.id);
        }
    }, [settings]);




    // calc document.
    useEffect(() => {
        if (settings && incomes) {
            calcDocument();
        }
    }, [settings, incomes]);




    // when user change document type: reset forms and update document settings and structure.
    useEffect(() => {

        if (formDocumentSettingsId) {
            resetAllData(formDocumentSettingsId);
        }

    }, [formDocumentSettingsId]);




    // calc total payment of documents closing
    useEffect(() => {

        const subscription = documentForm.watch((value, { name }) => {

            if (name === "receipt") {
                calcDocumentsClosing(Number(value[name]));
            }
        });

        return () => subscription.unsubscribe();

    }, [documentForm.watch("receipt")]);




    // ###################################################################################### //
    // #                      Fetch Data Functions                                          # //
    // ###################################################################################### //


    /**
     * Fetch the next document number by document settings id.
     * Then updates the form.
     */
    async function setDocumentNumber(settingsId: number): Promise<void>{
        try {
            const documentNumber: number = await getNextDocumentNumber(settingsId);
            documentForm.setValue("document_number", documentNumber);
        } 
        catch (error) {
            handleApiErrors(error, showBoundary);
        }
    }







    // ###################################################################################### //
    // #                               Functions                                            # //
    // ###################################################################################### //

    
    /**
     * clean all data in the document page.
     */
    function resetAllData(documentSettingsId: number): void {

        documentForm.reset({
            document_number: undefined,
            document_account: null,
            car: null,
        });

        worksForm.reset();
        itemsForm.reset();
        paymentsForm.reset();
        documentClosingForm.reset({ documents: [] });
        documentDepositsForm.reset({ deposits: [] });

        navigate(`/documents/add?documentType=${documentSettingsId}`, {
            replace: true,
            state: { 
                breadcrumbData: [
                    {
                        name: "מסמכים",
                        link: "/documents"
                    },
                    {
                        name: "הוספת מסמך",
                    },
                    {
                        name: `${documentsSettings?.find(documentsSetting => documentsSetting.id === documentSettingsId)?.name}`,
                    }
                ] 
            }
        });
    }



    // *********************************************************************************************


    /**
     * update the document calculation section
     */
    function calcDocument(): void {

        const works = worksForm.getValues().works;
        const items = itemsForm.getValues().items;
        const payments = paymentsForm.getValues().payments;

        calcWorksAndItems(documentForm, works, items, settings, incomes);
        calcKabala(documentForm, payments, settings, documentsToClose);
    }


    // *********************************************************************************************



    


    // *********************************************************************************************


    function calcDocumentsClosing(receipt: number) {

        let balance = receipt;

        const updateForm = documentClosingForm.getValues("documents").map(documentClosing => {

            if (documentClosing.old_balance <= balance) {

                const documentClosingToRetrun: DocumentClosingForm = {
                    ...documentClosing,
                    balance: 0,
                    status: 'סגור',
                    amount: documentClosing.balance,
                };

                balance = balance - documentClosing.balance;

                return documentClosingToRetrun;
            }

            else if (documentClosing.old_balance > balance && balance !== 0) {

                const updated_balance = documentClosing.old_balance - balance
                const documentClosingToRetrun: DocumentClosingForm = {
                    ...documentClosing,
                    balance: updated_balance,
                    status: 'סגור חלקית',
                    amount: balance,
                };

                balance = 0;

                return documentClosingToRetrun;
            }

            else if (balance === 0) {

                const documentClosingToRetrun: DocumentClosingForm = {
                    ...documentClosing,
                    balance: documentClosing.balance,
                    status: 'פתוח',
                    amount: balance,
                };

                return documentClosingToRetrun
            }
        });

        documentClosingForm.reset({ documents: updateForm });

    }  
   


    // *********************************************************************************************


    // -------------------  add document functions -------------------------- //


    // *********************************************************************************************


    //add a new document with all the forms data
    const addDocument = async () => {

        if ((settings?.display_works || settings?.display_items) && documentForm.getValues("to_pay") === 0) {
            alert("הסכום לתשלום לא יכול להיות 0.");
            return;
        }

        if ((settings?.display_works || settings?.display_items) && settings?.display_payments && documentForm.getValues("balance") !== 0) {
            alert("ההפרש צריך להיות 0");
            return;
        }

        console.log("addDocument worksForm.getValues() ", worksForm.getValues());
        console.log("addDocument itemsForm.getValues() ", itemsForm.getValues());
        console.log("addDocument paymentsForm.getValues() ", paymentsForm.getValues());
        console.log("addDocument documentForm.getValues() ", documentForm.getValues());

        documentForm.setValue("account_id", documentForm.getValues("document_account.account_id"));
        documentForm.setValue("status", settings?.type === "מסמך רגיל" || settings?.type === "כרטיס עבודה" || settings?.type === "חשבונית מס" ? "פתוח" : "סגור");

        const updatedcustomerCustomData = documentForm.getValues("document_account.custom_data");
        const updatedcarCustomData = documentForm.getValues("car.custom_data");
        const updatedWorksCustomData = worksForm.getValues("works");
        const updatedItemsCustomData = itemsForm.getValues("items");

        updateCustomDataArray(updatedcustomerCustomData);
        updateCustomDataArray(updatedcarCustomData);

        updatedWorksCustomData.forEach((work) => {
            updateCustomDataArray(work.custom_data);
        });


        updatedItemsCustomData.forEach((item) => {
            updateCustomDataArray(item.custom_data);
        });

        documentForm.setValue("document_account.custom_data", updatedcustomerCustomData);
        documentForm.setValue("car.custom_data", updatedcarCustomData);
        worksForm.setValue("works", updatedWorksCustomData);
        itemsForm.setValue("items", updatedItemsCustomData);

        if (!settings?.display_car || !documentForm.getValues("car.account_number")) {
            documentForm.setValue("car", undefined);
        }



        try {

            //add works to documentForm
            if (settings?.display_works) {

                await new Promise<void>((resolve, reject) => {
                    worksForm.handleSubmit((data: { works: DocumentWorkAdd[] }) => {
                        const works = data.works.filter((work) => !(work.total === 0 && work.name === "" && work.work_code === ""));
                        documentForm.setValue("works", works);
                        resolve();
                    }, (errors) => {
                        reject(errors);
                    })();
                });
            }


            //add items to documentForm
            if (settings?.display_items) {

                await new Promise<void>((resolve, reject) => {
                    itemsForm.handleSubmit((data: { items: DocumentItemAdd[] }) => {
                        const items = data.items.filter((item) => !(item.total === 0 && item.name === "" && item.sku === ""));
                        documentForm.setValue("items", items);
                        resolve();
                    }, (errors) => {
                        reject(errors);
                    })();
                });
            }

            //add payments to documentForm
            if (settings?.display_payments) {

                await new Promise<void>((resolve, reject) => {
                    paymentsForm.handleSubmit((data: { payments: DocumentPaymentForm[] }) => {
                        const payments = data.payments.filter((payment) => !(payment.total === 0 && payment.kupa_account_id === null));
                        documentForm.setValue("payments", payments);
                        resolve();
                    }, (errors) => {
                        reject(errors);
                    })();
                });
            }

            //add documents closing to documentForm
            if (!settings?.display_items && !settings?.display_works && settings?.display_payments) {

                await new Promise<void>((resolve, reject) => {
                    documentClosingForm.handleSubmit((data: { documents: DocumentClosingForm[] }) => {
                        const documentsClosing = data.documents.filter((document) => document.status !== 'פתוח');
                        documentForm.setValue("documents_closing", documentsClosing);
                        resolve();
                    }, (errors) => {
                        reject(errors);
                    })();
                });
            }

            //add deposits to documentForm
            await new Promise<void>((resolve, reject) => {
                documentDepositsForm.handleSubmit((data: { deposits: DocumentDepositAdd[] }) => {
                    documentForm.setValue("deposits", data.deposits);
                    resolve();
                }, (errors) => {
                    reject(errors);
                    return;
                })();
            });

            console.log("addDocument documentForm.getValues() after adding forms", documentForm.getValues());

            // If All Forms have no errors, submit.
            await new Promise<void>((resolve, reject) => {
                documentForm.handleSubmit((data) => {
                    onSubmitDocument(data);
                    resolve();
                }, (errors) => {
                    reject(errors);
                    return;
                })();
            });


        } catch (error) {
            console.error('An error occurred during Document submission:', error);
            // Handle the error accordingly
        }

    };



    // *********************************************************************************************


    // submit the document after validation
    const onSubmitDocument: SubmitHandler<DocumentAdd> = async (documentData) => {

        setIsSaving(true);

        console.log("onSubmitDocument documentData:", documentData);


        const addPromise = async () => {
            const response = await addDocumentService(documentData);
            console.log("document added: ", response);

            setIsSaving(false);

            navigate(`/documents`, {
                replace: true,
                state: null
            });
        }

        // sending the form
        toast.promise(
            addPromise,
            {
                pending: 'מוסיף מסמך...',
                success: 'המסמך נוסף בהצלחה',
                error: {
                    render({ data }) {
                        // When the promise reject, data will contains the error
                        setIsSaving(false);
                        return toastErrorHandling(data)
                    }
                }
            }
        );
    }





    // ###################################################################################### //
    // #                                component jsx                                       # //
    // ###################################################################################### //





    return (
        <StyledDocument>

            {documentsSettings && settings && structure && documentsStructure && formDocumentNumber && incomes && kupaAccounts ? <>


                {/************************************************  כותרת מסמך  ************************************************/}

                <Title
                    method={method}
                    documentForm={documentForm}
                    worksForm={worksForm}
                    itemsForm={itemsForm}
                    paymentsForm={paymentsForm}
                    settings={settings}
                />

                {/************************************************  סוג מסמך, מספר, תאריך ושעה ************************************************/}

                <StyledSection>
                    <Header
                        method={method}
                        documentForm={documentForm}
                        documentsSettings={documentsSettings}
                        settings={settings}
                        structure={structure}
                    />
                </StyledSection>



                {/************************************************  פרטי לקוח ורכב / חשבון  ************************************************/}

                <div className="details">

                    {/************************************************  פרטי לקוח  ************************************************/}

                    {settings?.display_customer && (
                        <StyledSection>
                            <DocumentAccount
                                method={method}
                                documentForm={documentForm}
                                worksForm={worksForm}
                                itemsForm={itemsForm}
                                paymentsForm={paymentsForm}
                                settings={settings}
                                structure={structure}
                            />
                        </StyledSection>
                        
                    )}

                    {/************************************************  פרטי רכב  ************************************************/}

                    {settings?.display_car && (

                        <StyledSection>
                            <DocumentCar
                                method={method}
                                documentForm={documentForm}
                                worksForm={worksForm}
                                itemsForm={itemsForm}
                                paymentsForm={paymentsForm}
                                settings={settings}
                                structure={structure}
                            />
                        </StyledSection>
                        
                    )}

                    {/************************************************  פרטי בנק  ************************************************/}

                    {settings?.name === 'הפקדה' && (

                        <StyledSection>
                            <DocumentBank
                                method={method}
                                documentForm={documentForm}
                                worksForm={worksForm}
                                itemsForm={itemsForm}
                                paymentsForm={paymentsForm}
                                settings={settings}
                                structure={structure}
                            />
                        </StyledSection>
                        
                    )}
                </div>


                {/************************************************  עבודות  ************************************************/}

                {settings?.display_works && (

                    <StyledSection>
                        <DocumentWorks
                            method={method}
                            documentForm={documentForm}
                            worksForm={worksForm}
                            paymentsForm={paymentsForm}
                            itemsForm={itemsForm}
                            worksFieldArray={worksFieldArray}
                            settings={settings}
                            structure={structure}
                            calcDocument={calcDocument}
                        />
                    </StyledSection>
                    
                )}


                {/************************************************  פריטים  ************************************************/}

                {settings?.display_items && (

                    <StyledSection>
                        <DocumentItems
                            method={method}
                            documentForm={documentForm}
                            worksForm={worksForm}
                            paymentsForm={paymentsForm}
                            itemsForm={itemsForm}
                            itemsFieldArray={itemsFieldArray}
                            settings={settings}
                            structure={structure}
                            calcDocument={calcDocument}
                        />
                    </StyledSection>
                    
                )}

                {/************************************************  הצגת חשבוניות בקבלה  ************************************************/}

                {settings.name === "קבלה" && (

                    <StyledSection>
                        <Invoices
                            method={method}
                            paymentsForm={paymentsForm}
                            documentForm={documentForm}
                            documentClosingForm={documentClosingForm}
                            documentsClosingFieldArray={documentsClosingFieldArray}
                            settings={settings}
                            documentsToClose={documentsToClose}
                            setDocumentsToClose={setDocumentsToClose}
                        />
                    </StyledSection>
                    
                )}

                {/************************************************  בחירת אמצעי תשלום בקבלה  ************************************************/}

                {settings?.display_payments && kupaAccounts && (

                    <StyledSection>
                        <Payments
                            method={method}
                            paymentsForm={paymentsForm}
                            paymentsFieldArray={paymentsFieldArray}
                            kupaAccounts={kupaAccounts}
                            calcDocument={calcDocument}
                        />
                    </StyledSection>
                    
                )}

                {/************************************************  הצגת שיקים בהפקדה  ************************************************/}

                {settings.name === "הפקדה" && (

                    <StyledSection>
                        <Deposit
                            method={method}
                            documentForm={documentForm}
                            documentDepositsForm={documentDepositsForm}
                            depositsFieldArray={depositsFieldArray}
                            kupaAccounts={kupaAccounts}
                            isSelectDepositsDialogOpen={isSelectDepositsDialogOpen}
                            isSelectCashDialogOpen={isSelectCashDialogOpen}
                            setIsSelectDepositsDialogOpen={setIsSelectDepositsDialogOpen}
                            setIsSelectCashDialogOpen={setIsSelectCashDialogOpen}
                        />
                    </StyledSection>
                    
                )}



                {/************************************************  הצגת בועת סיכום  ************************************************/}

                <StyledSection>
                    <Summary
                        method={method}
                        documentForm={documentForm}
                        settings={settings}
                    />
                </StyledSection>
                

                {/************************************************  כפתורים לשמירה או חזור  ************************************************/}

                <div className="buttons">
                    <Button onClick={addDocument} variant='contained' color="primary" disabled={isSaving}>שמירה</Button>
                    {/* <Button variant='outlined' color="primary" onClick={()=>{navigate("/documents")}}>חזור</Button> */}
                    <ReturnButton/>
                </div>


                {/************************************************  הצגת טעינה  ************************************************/}

            </> : <Loading/>
            }

            {/* <DevTool control={documentForm.control} /> */}

        </StyledDocument >
    )
}

export default AddDocument